import { Center, Collapse, Flex, Image, List, Spinner, Text, useDisclosure } from '@chakra-ui/react'
import { Button } from '@ubirider/pick-component-library'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Trip } from './components/Trip'
import { NewBillingModal } from '../../../Wallet/components/BillingProfile/components/NewBillingModal/NewBillingModal'
import { SelectBillingModal } from '../../../Wallet/components/BillingProfile/components/SelectBillingModal/SelectBillingModal'
import { useBilling, useTrips } from '../../../../context/DataProvider'

import EmptyStateIllustration from '../../../../assets/bill_empty.svg'

// 1. Move variants outside to prevent re-definition on render
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 25 },
  },
}

const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
}

export const TravelHistoryTable = ({ home }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { trips, loading: tripsLoading } = useTrips(home)
  const { billing, loading: billingLoading } = useBilling()

  const loading = tripsLoading || billingLoading

  const [modalTrips, setModalTrips] = useState([])
  const [checkedTrips, setCheckedTrips] = useState([])

  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isOpenSB, onOpen: onOpenSB, onClose: onCloseSB } = useDisclosure()

  // 2. Simplified Selection Logic
  const handleCheckTrip = trip => {
    setCheckedTrips(prev =>
      prev.includes(trip) ? prev.filter(t => t !== trip) : prev.length < 5 ? [...prev, trip] : prev
    )
  }

  const handleOpenModal = tripsArg => {
    setModalTrips(tripsArg)
    billing?.length > 0 ? onOpenSB() : onOpen()
  }

  const handleCloseModal = () => {
    onClose()
    if (billing?.length > 0) onOpenSB()
  }

  const hasTrips = trips?.length > 0

  if (loading) {
    return (
      <Center minH='300px'>
        <Spinner thickness='4px' color='primary.500' size='xl' />
      </Center>
    )
  }

  return (
    <Flex
      as={motion.div}
      variants={containerVariants}
      initial='hidden'
      animate='visible'
      direction='column'
      gap='32px'
      w='100%'
    >
      <Flex
        direction='column'
        gap='24px'
        p={{ base: 0, md: '24px' }}
        bg={{ base: 'transparent', md: 'neutral.100' }}
        rounded='8px'
      >
        {/* Header Section */}
        <Flex as={motion.div} variants={itemVariants} align='center' justify='space-between'>
          <Text fontWeight='700' fontSize='24px'>
            {home ? t('acqua_vias.last_trips_title') : t('travel_history.travel_history')}
          </Text>
        </Flex>

        {!hasTrips ? (
          <EmptyState t={t} navigate={navigate} />
        ) : (
          <Flex direction='column' gap='24px'>
            <Text as={motion.p} variants={itemVariants} fontSize='14px' color='neutral.800'>
              {t('acqua_vias.last_trips_description')}
            </Text>

            {/* Action Buttons */}
            <Collapse in={checkedTrips.length > 0}>
              <Flex justify='flex-end' gap='16px' pb='16px'>
                <Button
                  onClick={() => handleOpenModal(checkedTrips)}
                  style='outline'
                  type='secondary'
                  label={t('travel_history.change_tax_id')}
                />
                <Button style='solid' type='secondary' label={t('travel_history.download_invoice')} />
              </Flex>
            </Collapse>

            {/* Rows */}
            <List as={motion.ul} variants={listVariants} display='flex' flexDirection='column'>
              {/* Table Header */}
              <TableHeaderLabels t={t} />
              {trips.map((trip, index) => (
                <motion.li key={trip.id || trip.date} variants={itemVariants} layout>
                  <Trip
                    trip={trip}
                    onChange={() => handleCheckTrip(trip)}
                    onEdit={() => handleOpenModal([trip])}
                    isLast={index === trips.length - 1}
                  />
                </motion.li>
              ))}
            </List>
          </Flex>
        )}

        {home && hasTrips && <Footer t={t} navigate={navigate} />}
      </Flex>

      <Modals
        onOpen={onOpen}
        onClose={handleCloseModal}
        isOpen={isOpen}
        onOpenSB={onOpenSB}
        onCloseSB={onCloseSB}
        isOpenSB={isOpenSB}
        modalTrips={modalTrips}
      />
    </Flex>
  )
}

// Sub-components to keep the main component clean
const TableHeaderLabels = ({ t }) => (
  <Flex
    as={motion.div}
    variants={itemVariants}
    w='100%'
    h='48px'
    pl='16px'
    pr='32px'
    align='center'
    bg='white'
    shadow='sm'
    border='1px solid #EAECF0'
    borderBottom={'0px'}
    borderTopRadius='8px'
  >
    <Flex flex='1' display={{ sm: 'none', lg: 'flex' }} />
    <Text fontSize='14px' fontWeight='700' flex={{ sm: '3', lg: '2' }}>
      {t('acqua_vias.date_column_header')}
    </Text>
    <Text fontSize='14px' fontWeight='700' flex='3' display={{ sm: 'none', lg: 'flex' }}>
      {t('acqua_vias.payment_method_column_header')}
    </Text>
    <Text fontSize='14px' fontWeight='700' flex={{ sm: '3', lg: '2' }} textAlign='center'>
      {t('acqua_vias.vehicle_column_header')}
    </Text>
    <Text fontSize='14px' fontWeight='700' flex='2' textAlign='center'>
      {t('acqua_vias.amount_column_header')}
    </Text>
    <Text fontSize='14px' fontWeight='700' flex='2' justifyContent='flex-end' display={{ sm: 'none', lg: 'flex' }}>
      {t('acqua_vias.payment_column_header')}
    </Text>
    <Flex flex='1' display={{ sm: 'flex', lg: 'none' }} />
  </Flex>
)

const EmptyState = ({ t, navigate }) => (
  <Flex
    as={motion.div}
    variants={itemVariants}
    direction='column'
    align='center'
    py='48px'
    gap='24px'
    textAlign='center'
  >
    <Image src={EmptyStateIllustration} h='112px' />
    <Text fontSize='14px' color='neutral.800'>
      {t('acqua_vias.last_trips_empty_state')}
    </Text>
  </Flex>
)

const Footer = ({ t, navigate }) => (
  <Flex as={motion.div} variants={itemVariants} align='center' justify='flex-end' pt='16px'>
    <Button
      onClick={() => navigate('/travel-history')}
      size='small'
      style='ghost'
      type='secondary'
      label={t('acqua_vias.last_trips_view_history')}
    />
  </Flex>
)

const Modals = ({ onOpen, onClose, isOpen, onOpenSB, onCloseSB, isOpenSB, modalTrips }) => (
  <>
    <NewBillingModal onOpen={onOpen} onClose={onClose} isOpen={isOpen} blueInfo />
    <SelectBillingModal onOpen={onOpenSB} onClose={onCloseSB} isOpen={isOpenSB} onOpenBM={onOpen} trips={modalTrips} />
  </>
)
